import Box from '@mui/material/Box';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';

import DividerWithText from '~components/DividerWithText/DividerWithText';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { useStores } from '~store';

import { ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE } from './constants';
import DispatchNoResultsEmptyState from './DispatchNoResultsEmptyState';

interface OrdersListFooterProps {
  onShowAllDates?: () => void;
  selectedDateRangeLabel?: string;
}

const OrdersListFooter = observer(
  ({ onShowAllDates, selectedDateRangeLabel }: OrdersListFooterProps) => {
    const { ordersDispatchStore } = useStores();
    const { filters, hasMore, hasUnfilteredResults, isResolvingSearchScope, orders } =
      ordersDispatchStore;

    // Hold the loading indicator through BOTH the orders fetch and the out-of-range probe (and
    // the brief post-auto-extend refetch gap) so the search-aware empty states below never
    // flash while the counts are still settling (TRE-15947).
    if (isResolvingSearchScope) {
      return <LoadingIndicator />;
    }

    if (!hasMore) {
      const isEmpty = orders.length === 0;
      // The min-height keeps either empty-state block visible as the list's last item.
      const trimmedSearchTerm = filters.search?.trim();
      const hasSearchWithNoOrders = isEmpty && Boolean(trimmedSearchTerm);
      const shouldShowNoResultsForTerm = hasSearchWithNoOrders && !hasUnfilteredResults;
      const shouldShowResultsOnOtherDates = hasSearchWithNoOrders && hasUnfilteredResults;

      if (shouldShowNoResultsForTerm) {
        return (
          <Box
            sx={{
              minHeight: 240,
              px: ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE,
            }}
          >
            <DispatchNoResultsEmptyState
              term={trimmedSearchTerm ?? ''}
              variant="nowhere"
            />
          </Box>
        );
      }

      if (shouldShowResultsOnOtherDates) {
        return (
          <Box
            sx={{
              minHeight: 240,
              px: ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE,
            }}
          >
            <DispatchNoResultsEmptyState
              dateLabel={selectedDateRangeLabel}
              onShowAllDates={onShowAllDates}
              term={trimmedSearchTerm ?? ''}
              variant="other-dates"
            />
          </Box>
        );
      }

      return (
        <Box
          sx={(theme) => ({
            minHeight: `calc(18px + ${theme.spacing(2)})`,
            px: ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE,
          })}
        >
          <DividerWithText
            text={
              isEmpty ? t('common.no_data') : t('dispatch.dispatch_v2.all_orders_loaded')
            }
          />
        </Box>
      );
    }

    return <Box sx={{ px: ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE, py: 1 }} />;
  },
);

function LoadingIndicator() {
  return (
    <Box
      sx={(theme) => ({
        alignItems: 'center',
        display: 'flex',
        height: `calc(30px + ${theme.spacing(2)})`,
        justifyContent: 'center',
        px: ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE,
      })}
    >
      <LoadingSpinner isVisible />
    </Box>
  );
}

export default OrdersListFooter;
